import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnInit,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { distinctUntilChanged, first, map } from 'rxjs/operators';
import { JotformApiService } from '../../services/jotfrom-api.service';
import {
  JotFormConditions,
  JotFormQuestion,
  JotFormControlTypes,
  JotFormWidgetTypes,
} from './jotfrom-types';
import { atLeastOneChecked } from '../../util/checkbox-required';
import { JotFormCloseModalComponent } from './close-modal/close-modal.component';
import { AnalyticsService } from '@geneplanet/ngx-utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'app-jotform-modal',
  templateUrl: './jotform-modal.component.html',
  styleUrls: ['./jotform-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JotFormModalComponent implements OnInit {
  @Input() formId: string;
  @Input() additionalParameters: any;
  @Input() analyticsId: string;

  @Output() completed: EventEmitter<string> = new EventEmitter();

  pageId = {
    start: 'welcome',
    complete: 'thankyou',
    saveStep: null,
  };

  selectedQuestion: JotFormQuestion;
  form: UntypedFormGroup;
  controlTypes = JotFormControlTypes;
  widgetTypes = JotFormWidgetTypes;
  filteredQuestionsLength: number;
  questionIndex = 1;
  thankPageActive = false;
  questions: JotFormQuestion[];

  private hiddenFields = {};
  private checkboxQuestions = [];
  private breadCrumbs = [];
  private conditions: object = {};
  private data = { formProperties: null, formQuestions: null };

  constructor(
    private cdr: ChangeDetectorRef,
    private jotformService: JotformApiService,
    public readonly modal: NgbActiveModal,
    private readonly modals: NgbModal,
    private analyticsService: AnalyticsService
  ) {}

  ngOnInit() {
    this.jotformService
      .questions(this.formId)
      .pipe(
        first(),
        map((questions: { content: object }) => {
          return Object.values(questions.content).sort(
            (a, b) => Number(a.order) - Number(b.order)
          );
        })
      )
      .subscribe((questions) => {
        this.data.formQuestions = questions;
        this.setQuestions();
      });

    this.jotformService
      .formProperties(this.formId)
      .pipe(first())
      .subscribe((properties) => {
        this.data.formProperties = properties.content;
        this.setFormConditions(properties.content.conditions);
        this.setQuestions();
      });
  }

  start() {
    this.questionIndex = 1;
    this.selectQuestion(this.questions[1]);
  }

  next(step = 1) {
    if (this.control.status !== 'VALID' && step > 0) {
      return;
    }

    const breadcrumb = step > 0;
    let next;

    if (step > 0) {
      if (this.isSaveStep) {
        this.saveAnswer();
      }

      next = this.questions[
        this.questions.indexOf(this.selectedQuestion) + step
      ];
      const rules = this.checkFormConditions(
        this.selectedQuestion.qid,
        this.control.value
      );

      next = rules ? this.findQuestion(rules) : next;
    } else {
      this.breadCrumbs.pop();
      next = this.findQuestion(this.breadCrumbs[this.breadCrumbs.length - 1]);
    }

    if (next) {
      this.questionIndex += step;
      this.selectedQuestion = null;
      this.cdr.detectChanges();
      this.selectQuestion(next, breadcrumb);
      this.cdr.markForCheck();
    }
  }

  get selectedQuestionOrder(): number {
    return Number(this.selectedQuestion?.order);
  }

  get control() {
    return this.form?.controls[this.selectedQuestion.qid];
  }

  public get isSaveStep() {
    return this.selectedQuestion.qid === this.pageId.saveStep;
  }

  getOption(option: string) {
    return (this.control as UntypedFormGroup).controls[option];
  }

  getAnswers(answers: string) {
    return answers.split('|');
  }

  close() {
    if (
      this.selectedQuestion.qid === this.pageId.start ||
      this.selectedQuestion.qid === this.pageId.complete
    ) {
      this.modal.dismiss();
    } else {
      const ref = this.modals.open(JotFormCloseModalComponent, {
        backdrop: 'static',
        keyboard: false,
        windowClass: 'small-modal',
      });

      ref.componentInstance.analyticsId = this.analyticsId;

      ref.result.then((result: any) => {
        if (result) {
          this.modal.dismiss();
        }
      });
    }
  }

  getFieldOptions() {
    const fieldOptions = JSON.parse(this.selectedQuestion.fieldParameters)?.[0];

    if (!fieldOptions) {
      return [];
    }
    return fieldOptions.options?.split(',') ?? [];
  }

  public saveAnswer() {
    const data = this.form.getRawValue();

    // Set checkbox values to array from a object with boolean properties
    this.checkboxQuestions.forEach((question) => {
      data[question] = Object.keys(data[question]).filter((key) => {
        return data[question][key];
      });
    });

    this.jotformService
      .saveSubmissions(this.formId, { ...data, ...this.hiddenFields })
      .subscribe((res) => this.completed.emit(res.content.submissionID));

    if (!this.thankPageActive) {
      this.modal.dismiss();
    }
  }

  private setQuestions() {
    if (this.data.formProperties && this.data.formQuestions) {
      this.questions = this.filterHiddenQuestions(this.data.formQuestions);

      this.form = this.toFormGroup(this.questions);

      const welcomePage = this.data.formProperties.welcomePage[0];
      const thankYouPage = this.data.formProperties.thankYouPage[0];

      if (Number(welcomePage.isActive)) {
        this.questions.unshift({
          ...welcomePage,
          qid: this.pageId.start,
        });
        this.questionIndex = 0;
      }

      const saveStep = this.questions[this.questions.length - 1];
      this.pageId.saveStep = saveStep.qid;

      if (Number(thankYouPage.isActive)) {
        this.questions.push({
          ...thankYouPage,
          qid: this.pageId.complete,
        });
        this.thankPageActive = true;
      } else if (saveStep.type === this.controlTypes.HEAD) {
        this.filteredQuestionsLength--;
      }

      this.selectQuestion(this.questions[0]);

      this.cdr.markForCheck();
    }
  }

  private filterHiddenQuestions(
    questions: JotFormQuestion[]
  ): JotFormQuestion[] {
    const filtered = [];

    questions.forEach((question) => {
      if (question.hidden === 'Yes') {
        if (this.additionalParameters[question.name]) {
          this.hiddenFields[question.qid] = this.additionalParameters[
            question.name
          ];
        }
      } else {
        filtered.push(question);
      }
    });

    this.filteredQuestionsLength = filtered.length;

    return filtered;
  }

  private selectQuestion(question: JotFormQuestion, breadcrumb = true) {
    this.selectedQuestion = question;

    if (breadcrumb) {
      this.breadCrumbs.push(question.qid);
    }
  }

  private setFormConditions(conditions: JotFormConditions[]) {
    if (!conditions) {
      return;
    }

    for (const condition of conditions) {
      const term = JSON.parse(condition.terms)[0];
      const action = JSON.parse(condition.action)[0];

      this.conditions[term.field] = {
        value: term.value,
        skipTo: action.skipTo,
      };
    }
  }

  private toFormGroup(questions: JotFormQuestion[]) {
    const group: any = {};

    for (const question of questions) {
      let validators = [Validators.required];

      if (
        [
          JotFormControlTypes.HEAD as string,
          JotFormControlTypes.TEXT as string,
        ].includes(question.type)
      ) {
        validators = [];
      }

      if (question.type === JotFormControlTypes.CHECKBOX) {
        this.checkboxQuestions.push(question.qid);
        const optionsArray = this.getAnswers(question.options);

        const formGroup = new UntypedFormGroup({}, atLeastOneChecked);
        optionsArray.forEach((option) => {
          const control = new UntypedFormControl(false);
          control.valueChanges
            .pipe(untilDestroyed(this))
            .subscribe((value) =>
              this.analyticsService.trackFormValue(
                this.analyticsId + '-questionnaire',
                "value",
                {
                  id: option,
                  value,
                },
                this.formId,
                question.name
              )
            );
          formGroup.addControl(option, control);
        });

        formGroup.updateValueAndValidity();

        group[question.qid] = formGroup;
      } else {
        group[question.qid] = new UntypedFormControl(
          null,
          Validators.compose(validators)
        );
        group[question.qid].valueChanges
          .pipe(distinctUntilChanged(), untilDestroyed(this))
          .subscribe((value) =>
            this.analyticsService.trackFormValue(
              this.analyticsId + '-questionnaire',
              "value",
              {
                value: value || '',
                isValid: group[question.qid].valid
              },
              this.formId,
              question.name
            )
          );
      }
    }
    return new UntypedFormGroup(group);
  }

  private findQuestion(qid: string) {
    return this.questions.find((item) => item.qid === qid);
  }

  private checkFormConditions(qid: string, value: string) {
    const rule = this.conditions[qid];

    if (rule?.value && rule.value === value) {
      return rule.skipTo;
    }

    return false;
  }
}
